<!--
@component
MatchStrip/Placeholder

Per-row placeholder for `MatchStrip`. Matches the rendered strip's footprint
exactly (circular flag + team code + middle slot + team code + circular flag),
so when `LazyMount` mounts the real `MatchStrip` no layout shift occurs.

Used as the `placeholder` snippet for `LazyMount` wherever a `MatchStrip` is
the lazy child (e.g. schedule list, group results). Kept purely visual: no
state, no images, no observers — the whole point is that hundreds of these
can render up front for essentially free.
-->

<script lang="ts">
  type Props = {
    /**
     * Accessible label announced once for whichever ancestor list this
     * placeholder sits inside. Defaults to a generic "loading" message.
     * Pass `null` to omit the live-region wrapper entirely when the
     * parent already labels the loading state.
     */
    label?: string | null;
  };

  let { label = 'Loading match' }: Props = $props();
</script>

{#snippet card()}
  <div
    class="placeholder match-strip-placeholder font-sans flex flex-row items-center justify-between"
    style="--h: 3rem;"
    aria-hidden="true"
  >
    <!-- Home team (flag + code) -->
    <div class="team flex items-center justify-start">
      <div class="flag"></div>
      <div class="team-name"></div>
    </div>

    <!-- Middle slot (date/time or score) -->
    <div class="middle"></div>

    <!-- Away team (code + flag) -->
    <div class="team flex items-center justify-end">
      <div class="team-name"></div>
      <div class="flag"></div>
    </div>
  </div>
{/snippet}

{#if label}
  <div role="status" aria-busy="true" aria-live="polite" aria-label={label}>
    {@render card()}
    <span class="visually-hidden">{label}</span>
  </div>
{:else}
  {@render card()}
{/if}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  // Mirror MatchStrip's real card dimensions so swapping in the live strip
  // doesn't shift surrounding layout.
  .placeholder.match-strip-placeholder {
    position: relative;
    background-color: var(--theme-colour-muted-grey);
    width: 100%;
    height: var(--h);
    border: 1px solid var(--theme-colour-muted-grey);
    border-radius: calc(10 * var(--theme-style-border-radius));
    overflow: hidden;

    // Shimmer overlay on the card body itself, so the whole strip
    // (background, faux flags, faux text) shines together.
    &:before {
      content: '';
      position: absolute;
      inset: 0;
      pointer-events: none;
      mix-blend-mode: multiply;
      @include mixins.loading;
    }
  }

  .team {
    gap: clamp(0.56rem, 0.52rem + 0.21vw, 0.69rem);
    z-index: 1;
  }

  // Solid circle that matches the rendered Flag's footprint exactly.
  .flag {
    width: var(--h);
    height: var(--h);
    border-radius: var(--theme-style-border-radius-pill);
    background-color: var(--theme-colour-tan-grey);
    outline: 1px solid var(--theme-colour-white);
    flex-shrink: 0;
  }

  // Faux team code rectangle (~3rem min-width, ~1rem tall).
  .team-name {
    width: 2.25rem;
    height: 0.75rem;
    border-radius: var(--theme-style-border-radius);
    background-color: var(--theme-colour-tan-grey);
    min-width: 3rem;
  }

  // Middle slot (score or time). Slightly wider to cover both cases.
  .middle {
    width: 3rem;
    height: 0.75rem;
    border-radius: var(--theme-style-border-radius);
    background-color: var(--theme-colour-tan-grey);
    z-index: 1;
  }
</style>
